import { type MouseEvent, useState } from "react"
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDollarSign } from "@fortawesome/pro-regular-svg-icons"
import { faEllipsisV } from "@fortawesome/pro-solid-svg-icons"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { useMoney } from "../../../../../../currency/useMoney"
import { toDineroOrUndefined } from "../../../../../../currency/currencyLib"
import OrderStatusBadge from "../../../../../../../shared/components/badges/OrderStatusBadge"
import { SimpleStatusChip } from "../../../../../../merchant/merchant/production/meetingService/components/StatusMenu"
import { Receipt, ReceiptDialog } from "../../../../../../receipt/pages/ReceiptDialog"
import { PurchaseOrder } from "../../../weekMenuTypes"

const useStyles = makeStyles()((theme) => ({
    row: {
        display: "flex",
        alignItems: "stretch",
        padding: "1rem 0",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    dateBlock: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        minWidth: "3rem",
    },
    dateDay: {
        color: theme.palette.tailwind.slate[950],
        fontSize: "1rem",
        fontWeight: 500,
    },
    dateMonth: {
        color: theme.palette.tailwind.zinc[950],
        fontSize: ".875rem",
        fontWeight: 400,
        textTransform: "lowercase",
    },
    separator: {
        width: ".1875rem",
        background: theme.palette.tailwind.blue[200],
        margin: "0 1rem",
        borderRadius: ".09375rem",
    },
    content: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: ".375rem",
    },
    titleLine: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: ".5rem",
    },
    title: {
        color: theme.palette.tailwind.slate[950],
        fontSize: "1rem",
        fontWeight: 500,
    },
    rightControls: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
    },
    menuButton: {
        width: "1.25rem",
        height: "1.25rem",
    },
    menuIcon: {
        color: theme.palette.tailwind.slate[400],
    },
    metaRow: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        rowGap: ".25rem",
        color: theme.palette.tailwind.slate[500],
    },
    metaItem: {
        display: "inline-flex",
        alignItems: "center",
        marginRight: ".75rem",
        fontSize: ".875rem",
        fontWeight: 400,
        "& .MuiTypography-root": {
            fontSize: ".875rem",
            fontWeight: 400,
        },
    },
    metaIconWrap: {
        width: ".75rem",
        height: ".75rem",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: ".25rem",
    },
    metaIcon: {
        width: "100%",
        height: "100%",
    },
    menu: {
        "& .MuiPaper-root": {
            width: "10rem",
        },
    },
}))

type PurchaseRowProps = Readonly<{
    purchase: PurchaseOrder
    purchases: ReadonlyArray<PurchaseOrder>
}>

export function PurchaseRow({ purchase, purchases }: PurchaseRowProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [receipt, setReceipt] = useState<Receipt>()

    const { id, kitchen, displayName, amount, deliveries, orderStatus, orderType, uid, deliveryDetails } = purchase
    const statusDetails = deliveryDetails?.statusDetails
    const deliveryTimeStr = deliveries?.[0]?.deliveryTime
    const deliveryDate = deliveryTimeStr ? dateTimeFactory.fromApi(deliveryTimeStr) : null
    const priceDinero = toDineroOrUndefined(amount)
    const title = kitchen?.name ?? displayName

    function handleMenuOpen(event: MouseEvent<HTMLElement>) {
        setAnchorEl(event.currentTarget)
    }
    function handleMenuClose() {
        setAnchorEl(null)
    }
    function handleShowReceipt() {
        setReceipt({ orderId: id, orderUid: uid, orderType })
        handleMenuClose()
    }

    return (
        <>
            <Box className={classes.row}>
                <Box className={classes.dateBlock}>
                    {deliveryDate?.isValid && (
                        <>
                            <Typography variant="h5" className={classes.dateDay}>
                                {dateTimeFactory.formatCustom(deliveryDate, "d")}
                            </Typography>
                            <Typography variant="h6" className={classes.dateMonth}>
                                {dateTimeFactory.formatCustom(deliveryDate, "MMM")}
                            </Typography>
                        </>
                    )}
                </Box>

                <Box className={classes.separator} />

                <Box className={classes.content}>
                    <Box className={classes.titleLine}>
                        <Typography variant="h6" className={classes.title}>
                            {title}
                        </Typography>
                        <Box className={classes.rightControls}>
                            {statusDetails && (
                                <SimpleStatusChip
                                    status={statusDetails.status}
                                    tooltipLabel={statusDetails.message}
                                    size="medium"
                                />
                            )}
                            {orderStatus && <OrderStatusBadge status={orderStatus} />}
                            <IconButton size="small" className={classes.menuButton} onClick={handleMenuOpen}>
                                <FontAwesomeIcon icon={faEllipsisV} className={classes.menuIcon} />
                            </IconButton>
                        </Box>
                    </Box>

                    <Box className={classes.metaRow}>
                        <Typography variant="h6" component="span" className={classes.metaItem}>
                            #{id}
                        </Typography>
                        {priceDinero && (
                            <Box component="span" className={classes.metaItem}>
                                <Box component="span" className={classes.metaIconWrap}>
                                    <FontAwesomeIcon icon={faDollarSign} className={classes.metaIcon} />
                                </Box>
                                <Typography variant="h6" component="span">
                                    {moneyFactory.format(priceDinero)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                className={classes.menu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <LocalizedStrict id="user-home-canteen-upcoming-orders-receipt-item">
                    <MenuItem onClick={handleShowReceipt}>Receipt</MenuItem>
                </LocalizedStrict>
            </Menu>

            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={purchases}
                    open={receipt !== undefined}
                    onClose={() => setReceipt(undefined)}
                />
            )}
        </>
    )
}
